import { z } from "zod";

import { CampaignStatus } from "@/enum/campaign-status.enum";
import { ScheduleFailureCategory, ScheduleProcessingState } from "@/enum/schedule.enum";

import { ConversationSchema } from "../../Apollo/schema/get-people";

export const ListPersonScheduleSchema = z.object({
	active: z.boolean(),
	createdAt: z.string().transform((arg) => new Date(arg)),
	deletedAt: z.string().nullable(),
	emailId: z.number(),
	email: z.object({
		email: z.string(),
	}),
	failedAt: z.string().nullable(),
	// Why the step failed, classified server-side from the persisted error payload (which is
	// deliberately not exposed). Null unless `failedAt` is set. See `outbox.failure.*` for wording.
	failureCategory: z.nativeEnum(ScheduleFailureCategory).nullable().optional(),
	// For a send the blacklist blocked: the company's own entry that matched (PREC-2548).
	blockedBy: z
		.object({ scope: z.string(), value: z.string(), origin: z.string().nullable() })
		.nullable()
		.optional(),
	// For a step not sent because its message kept a column placeholder: the columns the lead had
	// no value for (PREC-4440).
	missingColumns: z.array(z.string()).nullable().optional(),
	id: z.number(),
	lastActivity: z.string(),
	listPersonId: z.number(),
	processedAt: z.string().nullable(),
	canceledAt: z.string().nullable(),
	bouncedAt: z.string().nullable(),
	// Set on the rows canceled because a colleague at the lead's company answered us
	// (PREC-3855/PREC-3895). It is the reason marker on a terminal stop — paired with `canceledAt`,
	// it is the only signal distinguishing "stopped for a colleague" from a finished sequence.
	sameCompanyHeldAt: z.string().nullable().optional(),
	// The return date an out-of-office auto-reply announced (PREC-3894). On a pending row the step
	// was moved out to that day; on a canceled row the return was past the cap and the outreach was
	// ended. Without it a lead that quietly slipped a fortnight reads as an ordinary scheduled one.
	outOfOfficeUntil: z.string().nullable().optional(),
	publishedAt: z.string().nullable(),
	processingState: z.nativeEnum(ScheduleProcessingState).nullable(),
	scheduledFor: z.string().nullable(),
	scheduler: z.string().nullable(),
	updatedAt: z.string(),
	stepId: z.string().nullable(),
	stepType: z.string().optional(),
	userId: z.number(),
	campaignId: z.string().nullable(),
	// Run status of this schedule's OWN campaign (PREC-3249). Authoritative for "paused" display —
	// the List.campaignStatus rollup can't see a single paused campaign on an otherwise-running list.
	// Null for legacy/non-campaign schedules.
	campaignStatus: z.nativeEnum(CampaignStatus).nullable().optional(),
	conversationId: z.number().nullable(),
	conversation: ConversationSchema.nullable(),
	// Set while a manual follow-up waits behind a LinkedIn invitation gate (PREC-4220): the row has
	// no send time yet and receives one when the invite is accepted. Its processingState is
	// `pending`, not `scheduled` — every control predicate has to admit that state.
	gatedByScheduleId: z.number().nullable().optional(),
	// For a parked follow-up: the delay, in days after acceptance, the release applies.
	scheduledInDays: z.number().nullable().optional(),
});

export type ListPersonSchedule = z.infer<typeof ListPersonScheduleSchema>;
